/* eslint-disable functional/prefer-tacit */
import { useCallback, useEffect } from 'react';
import type {
  getStateFromPath,
  NavigationProp,
  NavigationState,
  RouteProp,
} from '@react-navigation/native';
import {
  useNavigation,
  useNavigationState,
  useRoute,
} from '@react-navigation/native';

import { appNavigationLinking } from './AppNavigation.linking';
import type {
  AppLinkingOptions,
  AppStackParamList,
  MainTabName,
  MainTabsParamList,
} from './AppNavigation.props';
import { useActiveRouteName } from './useActiveRouteName';

export const useAppNavigation = () =>
  useNavigation<NavigationProp<AppStackParamList>>();

export const useAppRoute = () => useRoute<RouteProp<AppStackParamList>>();

type AppStateSelector<T> = (state: NavigationState<AppStackParamList>) => T;

export const useAppNavigationState = <T>(selector: AppStateSelector<T>) =>
  useNavigationState<AppStackParamList, T>(selector);

export type DefaultState = ReturnType<typeof getStateFromPath>;
export type ModifiedState = DefaultState;

/**
 * Returns true if the specified route name is a screen
 * in the specified tab's stack.
 *
 * @param routeName The route name to test.
 * @param tabName Name of the main tab.
 * @param linkingOptions
 * @returns Boolean indicating whether
 */
export const isRouteInMainTab = (
  tabName: MainTabName,
  routeName: string,
  linkingOptions: AppLinkingOptions = appNavigationLinking,
) => {
  const mainTabsConfig = linkingOptions.config?.screens.MainTabs;

  if (mainTabsConfig === undefined || typeof mainTabsConfig === 'string')
    return false;

  const mainTabsScreens = mainTabsConfig.screens;

  if (mainTabsScreens === undefined) return false;

  const stackConfig = mainTabsScreens[tabName];

  if (stackConfig === undefined || typeof stackConfig === 'string')
    return false;

  const stackScreens = stackConfig.screens;

  if (stackScreens === undefined) return false;

  return Object.keys(stackScreens).includes(routeName);
};
// ─────────────────────────────────────────────────────────────────

// If we are in a modal screen and we navigate to the main tabs,
// We need first to dismiss the modal screen, then navigate to the main tabs,
// But since react-navigation debounces our calls, only considering the last one,
// We also need the setTimeout to ensure it actually does the intended steps,
// Native uses useLinking.native.tsx which doesn't have all of this code,
// So we only need to do this on web for useLinking.tsx.
export const useNavigateToMainTabs = (
  passedNavigation?: ReturnType<typeof useNavigation>,
) => {
  const fallbackNavigation = useNavigation<NavigationProp<MainTabsParamList>>();
  const typedNavigation = passedNavigation as typeof fallbackNavigation;
  const navigation = typedNavigation ?? fallbackNavigation;
  const activeRouteName = useActiveRouteName();

  return useCallback(
    async (
      screen: MainTabName,
      params: MainTabsParamList[MainTabName] = defaultScreenParams,
    ): Promise<void> => {
      return new Promise((resolve) => {
        const isOnModalScreen = modalScreensNames.has(activeRouteName);
        const shouldDismissTheCurrentStack =
          isOnModalScreen && navigation?.getParent;

        if (shouldDismissTheCurrentStack) {
          navigation.getParent()?.goBack();

          // We need to timeout the next navigation so it isn't debounced.
          setTimeout(() => {
            navigation.navigate(screen, params);
            resolve();
          });

          return;
        }

        navigation.navigate(screen, params);
        resolve();
      });
    },
    [activeRouteName, navigation],
  );
};

// This is a temporary fix for an issue with tab navigation.
// https://github.com/react-navigation/react-navigation/issues/10481#issuecomment-1117625967
export const useEmptyPageTabNavigationFix = () => {
  useEffect(() => {
    if (!window?.history?.pushState) return;
    window.history.pushState({}, '');
    window.history.pushState({}, '');
  }, []);
};

// ────────────────────────────────────────────────────────────────────────────────

const defaultScreenParams = {
  params: undefined,
  screen: undefined,
  state: undefined,
};

// ─── UTILS ──────────────────────────────────────────────────────────────────────

export const modalScreensNames = getModalScreensNames();
function getModalScreensNames() {
  const stackScreens = appNavigationLinking.config?.screens;
  const modalsConfig = stackScreens?.Modal;
  const hasPathConfig = typeof modalsConfig === 'object';
  const modalScreens = hasPathConfig ? modalsConfig.screens : null;

  return new Set(Object.keys(modalScreens ?? {}));
}
